/**
 * Custom element for expandable/collapsible content with a "Read more" button.
 * Controls visibility and state based on content height and user interaction.
 */
class BbdReadMore extends HTMLElement {
  /**
   * Initializes the component, reads attributes, and binds methods.
   */
  constructor() {
    super();

    /** @type {number} Maximum height before collapsing */
    this.threshold = parseInt(this.getAttribute('threshold'), 10) || 350;

    /** @type {string|null} Selector for the target content */
    this.targetSelector = this.getAttribute('target');

    /** @type {boolean} Whether the content is expanded */
    this.expanded = false;

    this.toggleReadMore = this.toggleReadMore.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  /**
   * Lifecycle: Called when the element is added to the DOM.
   * Sets up references, styles, and event listeners.
   */
  connectedCallback() {
    /** @type {HTMLButtonElement|null} */
    this.button = this.querySelector('button');

    /** @type {HTMLElement|null} */
    this.target = document.querySelector(this.targetSelector);

    if (!this.button || !this.target) return;

    this.target.classList.add('bbd-read-more__target');
    this.target.style.setProperty('--bbd-read-more-threshold', `${this.threshold}px`);
    this.button.addEventListener('click', this.handleClick);

    this.toggleReadMore();
    window.addEventListener('resize', this.toggleReadMore);
  }

  /**
   * Lifecycle: Called when the element is removed from the DOM.
   * Cleans up event listeners.
   */
  disconnectedCallback() {
    window.removeEventListener('resize', this.toggleReadMore);
    if (this.button) this.button.removeEventListener('click', this.handleClick);
  }

  /**
   * Handles the "Read more" button click.
   * Expands the content and updates state.
   * @private
   */
  handleClick() {
    this.toggleReadMore();

    this.expanded = true;
    this.target.style.setProperty('--bbd-read-more-threshold', '100%');
    this.button.setAttribute('aria-expanded', 'true');
  }

  /**
   * Checks if the content should be collapsed or expanded based on height.
   * Updates aria attributes and state accordingly.
   * @private
   */
  toggleReadMore() {
    if (this.expanded) return;

    if (this.target.scrollHeight > this.threshold) {
      this.button.setAttribute('aria-expanded', 'false');
      this.expanded = false;
    } else {
      this.button.setAttribute('aria-expanded', 'true');
      this.expanded = true;
    }
  }
}

if (!customElements.get('bbd-read-more')) {
  customElements.define('bbd-read-more', BbdReadMore);
}
